<template>
  <form-section :title="title">
    <template slot="controls">
      <content-actions
        v-model="value"
        :channel="$route.query.channel"
        import-enabled
        export-enabled
        :import-export-inactive="
          ![inAppTypes.MODAL, inAppTypes.HTML].includes(value.inapp_type) ||
            !$can('updateProjectItem')
        "
        apply-template-enabled
        copy-enabled
        :is-content-empty="disabled"
        :model="model"
      />
      <slot name="selected-template-info"></slot>
    </template>
    <el-row>
      <el-col :md="24" :lg="10" :xl="8">
        <form-item
          prop="inapp_type"
          :label="$t('CampaignForm_InApp_Field_Type')"
        >
          <enum-select
            :options="inAppAvailableTypes"
            v-model="value.inapp_type"
            :number="true"
            data-qa="inapp-type-dropdown"
          />
        </form-item>
        <form-item
          v-if="
            value.inapp_type !== inAppTypes.MODAL &&
              value.inapp_type !== inAppTypes.INLINE &&
              value.inapp_type !== inAppTypes.HTML
          "
          prop="inapp_orientation"
          :label="$t('CampaignForm_InApp_Field_Orientation')"
        >
          <enum-select
            :options="orientationLabels"
            v-model="value.inapp_orientation"
            :number="true"
            data-qa="inapp-orientation-dropdown"
          />
        </form-item>
        <el-tabs
          class="tabs"
          v-if="
            value.inapp_type !== inAppTypes.MODAL &&
              value.inapp_type !== inAppTypes.INLINE
          "
          :stretch="true"
          v-model="activeTab"
        >
          <el-tab-pane
            v-if="value.inapp_orientation !== 2"
            :label="$t('CampaignForm_InApp_Tab_Portrait')"
            name="portrait"
            :disabled="value.inapp_orientation === 2"
          />
          <el-tab-pane
            v-if="value.inapp_orientation !== 1"
            :label="$t('CampaignForm_InApp_Tab_Landscape')"
            name="landscape"
            :disabled="value.inapp_orientation === 1"
          />
        </el-tabs>
        <div
          v-if="
            value.inapp_type !== inAppTypes.HTML &&
              value.inapp_type !== inAppTypes.MODAL &&
              value.inapp_type !== inAppTypes.INLINE
          "
        >
          <form-item
            v-if="value.inapp_type === inAppTypes.BANNER"
            prop="position"
            :label="$t('CampaignForm_InApp_Field_Position')"
          >
            <enum-select
              :options="positionLabels"
              v-model="value.inapp.placeholder.position"
              data-qa="inapp-position-dropdown"
            />
          </form-item>
          <form-item
            v-if="value.inapp_type === inAppTypes.FULLSCREEN"
            prop="background"
            :label="$t('CampaignForm_InApp_Field_Upload')"
          >
            <campaign-image-upload
              class="upload"
              :value="value.inapp.placeholder[activeTab].background"
              :file.sync="value.inapp.placeholder[activeTab].background"
              :message-type="channelTypes.inapp"
              upload-type="fullsize"
              size="small"
              :on-success="onBackgroundImageUpload"
            />
          </form-item>
          <form-item
            prop="action"
            :label="$t('CampaignForm_InApp_Field_Action')"
          >
            <enum-select
              :options="actionLabels"
              v-model="value.inapp.placeholder.action"
              data-qa="inapp-action-dropdown"
            />
          </form-item>
          <form-item
            v-if="value.inapp.placeholder.action === actionTypes.DEEPLINK"
            prop="deeplink"
            :label="$t('CampaignForm_InApp_Field_Deeplink')"
          >
            <el-input v-model="value.inapp.placeholder.deeplink" />
          </form-item>
          <form-item
            v-if="value.inapp.placeholder.action === actionTypes.URL"
            prop="url"
            :label="$t('CampaignForm_InApp_Field_Url')"
          >
            <el-input v-model="value.inapp.placeholder.url" />
          </form-item>
          <el-row class="creatives" :key="key">
            <in-app-creative
              v-if="value.inapp_type === inAppTypes.BANNER"
              v-model="value.inapp.creatives[activeTab][item]"
              :key="index"
              :index="index"
              :on-delete="() => deleteCreative(item, activeTab)"
              v-for="(item, index) in Object.keys(
                value.inapp.creatives[activeTab]
              )"
            />
            <in-app-button
              :enable-checkbox="false"
              :text="false"
              v-if="value.inapp_type === inAppTypes.FULLSCREEN"
              v-model="value.inapp.buttons[item]"
              :active-tab="activeTab"
              :key="index"
              :index="index"
              :on-delete="() => deleteButton(item)"
              v-for="(item, index) in Object.keys(value.inapp.buttons)"
            />
          </el-row>
          <el-button
            class="m-top-24"
            style="width: 100%"
            type="primary"
            plain
            @click="createItem"
            icon="el-icon-plus"
            data-qa="inapp-add-btn"
          >
            {{ buttonLabel }}
          </el-button>
        </div>
        <form-item v-else-if="value.inapp_type === inAppTypes.HTML" prop="html">
          <el-input
            type="textarea"
            :rows="17"
            v-model="value.inapp.html"
            data-qa="inapp-html-input"
          >
          </el-input>
        </form-item>
        <div v-if="value.inapp_type === inAppTypes.INLINE">
          <native-inline-form :message="value.inapp" />
        </div>

        <el-tabs
          v-model="modalTab"
          v-else-if="value.inapp_type === inAppTypes.MODAL"
          class="tabs"
          :stretch="true"
        >
          <el-tab-pane name="form" :label="$t('CampaignForm_InApp_Tab_Form')">
            <draggable
              :list="sequenceList"
              class="list-group"
              ghost-class="ghost"
              filter=".redactor-wrapper, input"
              :prevent-on-filter="false"
            >
              <div v-for="item in sequenceList" class="collection-item">
                <el-row>
                  <el-col :span="21">
                    <el-row v-if="item.name === 'image'" key="image">
                      <el-col :span="3">
                        <form-item
                          prop="image_checkbox"
                          :label="$t('CampaignForm_InApp_Field_Image')"
                          class="image-checkbox"
                        >
                          <checkbox
                            title=""
                            label="image_checkbox"
                            v-model="value.inapp.editor.image_checkbox"
                          />
                        </form-item>
                      </el-col>
                      <el-col :span="21">
                        <form-item
                          prop="image"
                          label=" "
                          style="margin-bottom: 0"
                        >
                          <campaign-image-upload
                            class="upload"
                            :value="value.inapp.editor.image"
                            :file.sync="value.inapp.editor.image"
                            :message-type="channelTypes.inapp"
                            upload-type="modal"
                            size="small"
                          />
                        </form-item>
                        <form-item
                          prop="image_height"
                          :label="$t('CampaignForm_InApp_Field_Height')"
                        >
                          <el-input
                            v-model="value.inapp.editor.image_height"
                            :placeholder="
                              $t(
                                'CampaignForm_InApp_Field_ImageHeight_Placeholder'
                              )
                            "
                          />
                        </form-item>
                      </el-col>
                    </el-row>
                    <form-item
                      prop="title"
                      :label="$t('CampaignForm_InApp_Field_Title')"
                      v-if="item.name === 'title'"
                    >
                      <redactor
                        :key="titleKey"
                        v-model="value.inapp.editor.title"
                        :custom-config="{
                          tabKey: false,
                          enterKey: false,
                          maxHeight: 50
                        }"
                        :plugins="['alignment', 'personalization']"
                        data-qa="inapp-modal-title-input"
                      />
                    </form-item>
                    <form-item
                      v-if="item.name === 'text'"
                      key="text"
                      prop="main_text"
                      :label="$t('CampaignForm_InApp_Field_Text')"
                    >
                      <redactor
                        :key="textKey"
                        v-model="value.inapp.editor.main_text"
                        :buttons="['ol', 'ul']"
                        :plugins="['alignment', 'personalization']"
                        :custom-config="{
                          minHeight: 55,
                          maxHeight: 200
                        }"
                        data-qa="inapp-modal-text-input"
                      />
                    </form-item>
                    <el-row
                      v-if="item.name === 'buttons'"
                      key="buttons"
                      class="creatives"
                    >
                      <in-app-button
                        v-model="value.inapp.editor.button0"
                        :key="0"
                        :index="0"
                      />

                      <in-app-button
                        v-model="value.inapp.editor.button1"
                        :key="1"
                        :index="1"
                      />
                    </el-row>
                    <el-row v-if="item.name === 'inline-preferences'">
                      <el-col>
                        <form-section class="creative inline-preferences">
                          <inline-preference
                            v-model="value.inapp.editor.inline_preference"
                            inapp-styles
                          />
                        </form-section>
                      </el-col>
                    </el-row>
                  </el-col>
                  <el-col :span="3">
                    <i
                      class="icon el-icon-rank"
                      size="20"
                      style="font-size: 20px; line-height: 80px"
                    />
                  </el-col>
                </el-row>
              </div>
            </draggable>
          </el-tab-pane>
          <el-tab-pane
            name="colors"
            class="colors"
            :label="$t('CampaignForm_InApp_Tab_Colors')"
          >
            <el-row class="styles-section">
              <el-col :span="12">
                <color-picker-field
                  v-model="value.inapp.editor.color_container"
                  :label-text="$t('CampaignForm_InApp_Field_Overlay')"
                />
                <color-picker-field
                  v-model="value.inapp.editor.color_background"
                  :label-text="$t('CampaignForm_InApp_Field_Background')"
                />
                <color-picker-field
                  v-model="value.inapp.editor.color_title"
                  :label-text="$t('CampaignForm_InApp_Field_Title')"
                />
                <color-picker-field
                  v-model="value.inapp.editor.color_text"
                  :label-text="$t('CampaignForm_InApp_Field_Message')"
                />
              </el-col>
              <el-col :span="12">
                <form-item :label="$t('CampaignForm_InApp_Field_Opacity')">
                  <el-input-number
                    class="input-number"
                    v-model="value.inapp.editor.opacity_container"
                    controls-position="right"
                    :placeholder="
                      $t('CampaignForm_InApp_Field_Opacity_Placeholder')
                    "
                    :min="0"
                    :max="100"
                  />
                </form-item>
                <color-picker-field
                  v-model="value.inapp.editor.color_border"
                  :label-text="$t('CampaignForm_InApp_Field_Border')"
                />
                <form-item :label="$t('CampaignForm_InApp_Field_Radius')">
                  <el-input-number
                    class="input-number"
                    v-model="value.inapp.editor.color_border_radius"
                    controls-position="right"
                    :min="0"
                    :max="50"
                  />
                </form-item>
              </el-col>
            </el-row>
            <el-row class="styles-section">
              <el-col :span="12">
                <color-picker-field
                  v-model="value.inapp.editor.color_button_0_background"
                  :label-text="
                    $t('CampaignForm_InApp_Field_ButtonColor', { btn: '1' })
                  "
                />
                <color-picker-field
                  v-model="value.inapp.editor.color_button_0_border"
                  :label-text="$t('CampaignForm_InApp_Field_Border')"
                />
              </el-col>
              <el-col :span="12">
                <color-picker-field
                  v-model="value.inapp.editor.color_button_0_text"
                  :label-text="$t('CampaignForm_InApp_Field_Label')"
                />
              </el-col>
              <el-col :span="12"> </el-col>
            </el-row>
            <el-row class="styles-section">
              <el-col :span="12">
                <color-picker-field
                  v-model="value.inapp.editor.color_button_1_background"
                  :label-text="
                    $t('CampaignForm_InApp_Field_ButtonColor', { btn: '2' })
                  "
                />
                <color-picker-field
                  v-model="value.inapp.editor.color_button_1_border"
                  :label-text="$t('CampaignForm_InApp_Field_Border')"
                />
              </el-col>
              <el-col :span="12">
                <color-picker-field
                  v-model="value.inapp.editor.color_button_1_text"
                  :label-text="$t('CampaignForm_InApp_Field_Label')"
              /></el-col>
            </el-row>
            <el-row
              class="styles-section"
              v-if="value.inapp.editor.inline_preference.enabled"
            >
              <el-col :span="12">
                <color-picker-field
                  v-model="value.inapp.editor.color_inline_checkbox"
                  :label-text="$t('CampaignForm_InApp_Field_Inline_Checkbox')"
                />
                <color-picker-field
                  v-model="value.inapp.editor.color_inline_button_background"
                  :label-text="$t('CampaignForm_InApp_Field_Inline_Button')"
                />
                <color-picker-field
                  v-model="value.inapp.editor.color_inline_button_border"
                  :label-text="
                    $t('CampaignForm_InApp_Field_Inline_Button_Border')
                  "
                />
              </el-col>
              <el-col :span="12">
                <color-picker-field
                  v-model="value.inapp.editor.color_inline_text"
                  :label-text="$t('CampaignForm_InApp_Field_Inline_Text')"
                />
                <color-picker-field
                  v-if="value.inapp.editor.inline_preference.enabled"
                  v-model="value.inapp.editor.color_inline_button_text"
                  :label-text="
                    $t('CampaignForm_InApp_Field_Inline_Button_Text')
                  "
                />
              </el-col>
            </el-row>
          </el-tab-pane>
        </el-tabs>
      </el-col>
      <el-col :md="24" :lg="14" :xl="16" class="preview">
        <in-app-preview :message="value" :orientation="activeTab" key="" />
      </el-col>
    </el-row>
  </form-section>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import FormItem from '@/common/components/form/FormItem'
import InAppCreative from '@/modules/campaign-manage/components/setup/InAppCreative'
import InAppButton from '@/modules/campaign-manage/components/setup/InAppButton'
import EnumSelect from '@/common/components/form/EnumSelect'
import {
  inAppFormTypes,
  inAppTypes,
  actionTypes,
  typeLabels,
  orientationLabels,
  positionLabels,
  actionLabels,
  specialLabels,
  triggerLabels
} from '@/modules/campaign-manage/enum/InAppTypesEnum'
import Redactor from '@/common/components/Redactor'
import ColorPickerField from '@/common/components/form/ColorPickerField'
import InAppPreview from '@/modules/campaign-manage/components/InAppPreview'
import { EventBus } from '@/core/EventBus'
import { channelTypes } from '@/modules/campaign-manage/enum/Labels'
import draggable from 'vuedraggable'
import { projectEndpoint, querySearch } from '@/core/helpers'
import CampaignImageUpload from '@/modules/campaign-manage/components/content/CampaignImageUpload'
import RestApi from '@/core/services/RestApi'
import Checkbox from '@/common/components/form/Checkbox'
import ContentActions from '@/modules/campaign-manage/components/ContentActions'
import NativeInlineForm from '@/modules/campaign-manage/components/content/channels/NativeInlineForm'
import InlinePreference from '@/modules/campaign-manage/components/setup/InlinePreference'
import _ from 'lodash'
import { defaultMessages } from '@/modules/campaign-manage/enum/DefaultMessages'

export default {
  name: 'InApp',
  components: {
    InlinePreference,
    NativeInlineForm,
    ContentActions,
    Checkbox,
    CampaignImageUpload,
    draggable,
    InAppPreview,
    ColorPickerField,
    Redactor,
    EnumSelect,
    InAppButton,
    InAppCreative,
    FormItem,
    FormSection
  },
  data() {
    return {
      titleKey: '',
      textKey: '',
      listLoading: false,
      userListLoading: false,
      listOptions: [],
      userListOptions: [],
      creativeIndex: 2,
      buttonIndex: 2,
      key: '',
      channelTypes,
      sequenceList: [],
      modalTab: 'form',
      activeTab: 'portrait',
      header: {
        0: 'CampaignForm_InApp_Tab_Header_Banner',
        1: 'CampaignForm_InApp_Tab_Header_BackgroundImage',
        2: 'CampaignForm_InApp_Tab_Header_CustomHtml',
        3: 'CampaignForm_InApp_Tab_Header_Modal'
      },
      inAppTypes,
      typeLabels,
      actionTypes,
      orientationLabels,
      positionLabels,
      actionLabels,
      specialLabels,
      triggerLabels,
      inAppFormTypes: inAppFormTypes
    }
  },
  props: {
    value: {
      required: true
    },
    title: {
      required: true
    },
    model: {
      required: true
    }
  },
  beforeMount() {
    if (!this.value.inapp.editor.inline_preference) {
      let inlinePreference = _.cloneDeep(
        defaultMessages[channelTypes.inapp].inapp.editor.inline_preference
      )
      this.$set(this.value.inapp.editor, 'inline_preference', inlinePreference)
      _.forOwn(defaultMessages[channelTypes.inapp].inapp.editor, (val, key) => {
        if (key.search('color_inline') !== -1) {
          this.$set(this.value.inapp.editor, key, val)
        }
      })
    }
  },
  mounted() {
    EventBus.$on('applyPushTemplate-' + this.templateKey, data => {
      this.titleKey = new Date().getTime() + '_title'
      this.textKey = new Date().getTime() + '_text'
    })
    this.buttonIndex = Object.keys(this.value.inapp.buttons).length
    this.creativeIndex =
      Object.keys(this.value.inapp.creatives['portrait']).length +
      Object.keys(this.value.inapp.creatives['landscape']).length
    this.updateIndexes()
    this.fetchListsRemote()
    this.fetchUserListsRemote()
    this.setActiveTab(this.value.inapp_orientation)

    if (!this.value.inapp.editor.sequence) {
      this.value.inapp.editor.sequence = [
        'image',
        'title',
        'text',
        'buttons',
        'inline-preferences'
      ]
    } else if (
      !this.value.inapp.editor.sequence.includes('inline-preferences')
    ) {
      this.value.inapp.editor.sequence.push('inline-preferences')
    }
    this.sequenceList = this.value.inapp.editor.sequence.map(item => {
      return { name: item }
    })

    // Parse values from the old UI
    if (this.value.inapp.placeholder.add_to_list) {
      this.value.inapp.placeholder.add_to_list =
        parseInt(this.value.inapp.placeholder.add_to_list) || null
    }
    if (this.value.inapp.placeholder.subscribe_to) {
      this.value.inapp.placeholder.subscribe_to =
        parseInt(this.value.inapp.placeholder.subscribe_to) || null
    }
    setTimeout(() => {
      EventBus.$emit('updateInitialStateMessages', this.model.messages)
    })
  },
  computed: {
    templateKey() {
      return `${this.$route.query.channel}_${this.$route.query.variant}_${this.$route.query.lang}`
    },
    disabled() {
      if (this.value.inapp_type === inAppTypes.HTML && this.value.inapp.html) {
        return false
      }
      return !(
        this.value.inapp_type === inAppTypes.MODAL &&
        (this.value.inapp.editor.title ||
          this.value.inapp.editor.main_text ||
          this.value.inapp.editor.button0.text ||
          this.value.inapp.editor.button1.text)
      )
    },
    buttonLabel() {
      if (this.value.inapp_type === inAppTypes.BANNER) {
        return this.$t('CampaignForm_InApp_Button_AddCreative')
      } else if (this.value.inapp_type === inAppTypes.FULLSCREEN) {
        return this.$t('CampaignForm_InApp_Button_AddButton')
      }
      return ''
    },
    inAppAvailableTypes() {
      let types = { ...typeLabels }
      if (!this.$store.state.app.features.inapp_native) {
        this.$delete(types, inAppTypes.INLINE)
      }
      return types
    }
  },
  methods: {
    hidePopover(data) {
      const refRect = data.offsets.reference
      const headerHeight =
        document.querySelector('header > .top-container').offsetHeight || 64
      data.styles.display = refRect.bottom - headerHeight < 0 ? 'none' : 'block'
      return data
    },
    createItem() {
      if (this.value.inapp_type === 0) {
        this.addCreative(this.activeTab)
      } else if (this.value.inapp_type === 1) {
        this.addButton(this.activeTab)
      }
    },
    deleteCreative(index, orientation = 'portrait') {
      delete this.value.inapp.creatives[orientation][index]
      this.key = new Date().getTime()
    },
    addCreative(orientation) {
      let height = 0
      let width = 0
      if (!Object.keys(this.value.inapp.creatives[orientation]).length) {
        height = 50
        width = 320
      }
      const key = `creative_${this.creativeIndex++}`
      this.$set(this.value.inapp.creatives[orientation], key, {
        background_file: '',
        height: height,
        min_width: width
      })
      this.key = new Date().getTime()
    },
    deleteButton(index) {
      delete this.value.inapp.buttons[index]
      this.key = new Date().getTime()
    },
    addButton() {
      const key = `button_${this.buttonIndex++}`
      this.$set(this.value.inapp.buttons, key, {
        action: null,
        special: null,
        trigger: null,
        deeplink: null,
        url: null,
        trigger_event: null,
        add_to_list: null,
        text: null,
        set_attribute: {
          name: null,
          value: null
        },
        portrait: { background_file: '' },
        landscape: { background_file: '' }
      })
      this.key = new Date().getTime()
    },
    querySearchAttrs(queryString, cb) {
      return querySearch(`tags`, { sort: 'title', title: queryString }, cb)
    },
    fetchListsRemote(queryString) {
      this.listLoading = true
      return RestApi(
        projectEndpoint({
          method: 'get',
          url: `subscription-preferences`,
          params: { sort: 'name', name: queryString }
        })
      )
        .then(({ data }) => {
          this.listOptions = data
          this.listLoading = false
        })
        .catch(error => {
          this.listLoading = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },
    fetchUserListsRemote(queryString) {
      this.userListLoading = true
      return RestApi(
        projectEndpoint({
          method: 'get',
          url: `lists`,
          params: {
            sort: 'name',
            name: queryString,
            identifier: ['profile_id', 'device_id']
          }
        })
      )
        .then(({ data }) => {
          this.userListOptions = data
          this.userListLoading = false
        })
        .catch(error => {
          this.userListLoading = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },
    querySearchEvents(queryString, cb) {
      return querySearch(`events`, { sort: 'title', title: queryString }, cb)
    },
    onBackgroundImageUpload() {
      EventBus.$emit('inAppBackgroundUploaded')
    },
    updateIndexes() {
      // Update indexes for creatives and buttons
      let length = Object.keys(this.value.inapp.creatives['portrait']).length

      this.value.inapp.creatives['portrait'] = Object.values(
        this.value.inapp.creatives['portrait']
      ).reduce(
        (obj, item, index) => ({ ...obj, [`creative_${index}`]: item }),
        {}
      )

      this.value.inapp.creatives['landscape'] = Object.values(
        this.value.inapp.creatives['landscape']
      ).reduce(
        (obj, item, index) => ({
          ...obj,
          [`creative_${index + length}`]: item
        }),
        {}
      )

      this.value.inapp.buttons = Object.values(this.value.inapp.buttons).reduce(
        (obj, item, index) => ({ ...obj, [`button_${index}`]: item }),
        {}
      )
    },
    setActiveTab(orientation) {
      if (orientation === 1) {
        this.activeTab = 'portrait'
      }
      if (orientation === 2) {
        this.activeTab = 'landscape'
      }
    }
  },
  watch: {
    'value.inapp.native.payload_type': {
      handler(payloadType) {
        if (
          payloadType === 'json' &&
          !Array.isArray(this.value.inapp.native.payload.json)
        ) {
          this.value.inapp.native.payload.json = []
        }
      }
    },
    sequenceList: {
      handler(listDraggable) {
        this.value.inapp.editor.sequence = listDraggable.map(item => {
          return item.name
        })
      }
    },
    'value.inapp_orientation': {
      handler(orientation) {
        this.setActiveTab(orientation)
      },
      deep: true
    },
    'value.inapp_type': {
      handler(inappType) {
        if (inappType === inAppTypes.MODAL || inappType === inAppTypes.INLINE) {
          this.value.inapp_orientation = 1
        }
        if (inappType === inAppTypes.HTML) {
          this.value.inapp_orientation = 0
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.header {
  font-weight: 600;
  font-size: 14px;
  padding: 10px 0;
}
.preview {
  padding: 20px 0;
  background-color: #f8fafc;
  display: flex;
}
.tabs {
  padding-top: 30px;
  .image-checkbox {
    /deep/ .el-checkbox {
      padding-top: 10px;
    }
    width: 22px;
  }
}
.upload {
  width: 100%;
  /deep/ .el-upload {
    width: 100% !important;
  }
}
.redactor,
/deep/ .el-tabs__content,
.form-section {
  overflow: visible;
}
.draggable-item {
  border-radius: 4px;
  border: 1px solid #dbdee7;
}
.el-icon-rank {
  cursor: pointer;
}
.creatives {
  margin-top: 16px;
}

.p-top-136 {
  padding-top: 136px;
}
/deep/ .color-picker-ct {
  width: inherit !important;
}
/deep/ .el-input-number {
  width: 100%;
}
.input-number /deep/ input {
  height: 38px;
}
.inline-preferences {
  background-color: #f8fafc;
}
.colors .el-form-item {
  margin-bottom: 0px !important;
}
.styles-section {
  margin-bottom: 25px;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/content/channels/InApp.vue